/**
 * The frontend reader for the shared Excel export: gathers what the page already holds into the
 * `AurigaPageData` that `renderAurigaPage` (`@amzn/gft-auriga-excel-mapping`) turns into a workbook.
 *
 * It passes the same Deck Management shapes the report Lambda fetches from the API, read from the page's
 * working state, so both readers feed the mapping the same data:
 *  - page: the current granularity page, with the title the legacy export used;
 *  - layout: the page layout, with admin layout overrides already applied at fetch time;
 *  - decklines: the working copy, with unsaved edits applied and deleted rows already removed;
 *  - footnotes: the page footnotes, without footnotes deleted but not yet saved. This differs from the
 *    legacy export, which printed those too; dropping them matches the page on screen.
 *
 * The one intentional difference from the Lambda reader is preview values: they exist only in the
 * frontend's Preview Mode, so they are read back from the grid rows (`pageRowData`), which carry the seeded
 * numbers as `<letter>_<granularity>` fields. The Lambda never passes them.
 */
import type {
  AurigaDeckline,
  AurigaHeaderBlock,
  AurigaHeaderGroup,
  AurigaHeaderLeaf,
  AurigaLayoutSection,
  AurigaPageData,
  AurigaPageInfo,
} from '@amzn/gft-auriga-excel-mapping';
import type {
  BlockNode,
  Deckline,
  Footnote,
  GroupNode,
  LeafNode,
  Page,
  PageLayoutSection,
} from '@services/api/deckManagement/graphql/schema/API';
import { HeaderNodeKind } from '@services/api/deckManagement/graphql/schema/API';
import { type HeaderNodeShape, isGroupNode, resolveNodeShape } from '@utils/headerNodeShape';
import type { EnhancedDeckline } from 'types/DecklinesContext.types';
import type { EnhancedFootnote } from 'types/FootnotesContext.types';
import type { DecklineRowData, HeaderNode } from 'types/Page.types';

type AurigaFootnote = NonNullable<NonNullable<AurigaPageData['footnotes']>[number]>;

// ---- Conformance with the generated Deck Management types (the drift guard) ----
// The mapping declares the fields it reads by hand. These lines stop the build when a Deck Management
// codegen pull drops, renames or retypes one of them; update the mapping's `pageData.ts` to match.
type Missing<Mine, Theirs> = Exclude<keyof Mine, keyof Theirs>;
type Present<Mine, Theirs> = Missing<Mine, Theirs> extends never ? true : Missing<Mine, Theirs>;
const pageFieldsExist: Present<AurigaPageInfo, Page> = true;
const sectionFieldsExist: Present<AurigaLayoutSection, PageLayoutSection> = true;
const groupFieldsExist: Present<AurigaHeaderGroup, GroupNode> = true;
const blockFieldsExist: Present<AurigaHeaderBlock, BlockNode> = true;
const leafFieldsExist: Present<AurigaHeaderLeaf, LeafNode> = true;
const decklineFieldsExist: Present<AurigaDeckline, Deckline> = true;
const footnoteFieldsExist: Present<AurigaFootnote, Footnote> = true;
const fits = {
  page: (page: Page): AurigaPageInfo => page,
  section: (section: PageLayoutSection): AurigaLayoutSection => section,
  group: (group: GroupNode): AurigaHeaderGroup => group,
  block: (block: BlockNode): AurigaHeaderBlock => block,
  leaf: (leaf: LeafNode): AurigaHeaderLeaf => leaf,
  deckline: (deckline: Deckline): AurigaDeckline => deckline,
  footnote: (footnote: Footnote): AurigaFootnote => footnote,
};
/** Referenced so the checks above are not dead code; they only matter at compile time. */
export const AURIGA_EXPORT_CONFORMANCE = {
  fieldsExist: [
    pageFieldsExist,
    sectionFieldsExist,
    groupFieldsExist,
    blockFieldsExist,
    leafFieldsExist,
    decklineFieldsExist,
    footnoteFieldsExist,
  ],
  fits,
} as const;

/** What the page holds when Download is pressed. */
export interface PageExportState {
  /** The current granularity page (title, scale, grain). */
  page: Page | null | undefined;
  /** The core page name; the legacy export used it as the title when the page has no report header. */
  corePageName: string | null | undefined;
  /** The page layout sections, as rendered. */
  sections: ReadonlyArray<PageLayoutSection | null>;
  /** The working decklines (unsaved edits applied, deletions removed). */
  decklines: ReadonlyArray<EnhancedDeckline>;
  /** The page footnotes, including any deleted but not yet saved. */
  footnotes: ReadonlyArray<EnhancedFootnote>;
  /** The grid rows; read only for Preview Mode values. */
  rowData: ReadonlyArray<DecklineRowData>;
  /** The selected granularity, e.g. 'MTD'; preview fields are named `<letter>_<granularity>`. */
  granularity: string | null | undefined;
}

/**
 * List the layout's leaf column letters, in order, as the preview fields name them.
 *
 * @param sections - The page layout sections; a GROUP node's blocks and a top-level block are both read.
 *   Nodes are classified as the grid classifies them (`resolveNodeShape`: `__typename` first, `kind` as
 *   the fallback).
 * @returns The `excelLetter` of every leaf that has one (e.g. `['A', 'B', 'C']`).
 */
const letterOf = (sections: PageExportState['sections']): string[] =>
  sections.flatMap((section) =>
    (section?.columnHeaderTree ?? []).flatMap((node) => {
      if (!node) return [];
      const blocks: Array<BlockNode | null> = isGroupNode(node) ? (node.children ?? []) : [node as BlockNode];
      return blocks.flatMap((block) =>
        (block?.children ?? []).flatMap((leaf) => (leaf?.excelLetter ? [leaf.excelLetter] : [])),
      );
    }),
  );

const KIND_OF_SHAPE: Record<HeaderNodeShape, HeaderNodeKind> = {
  GROUP: HeaderNodeKind.GROUP,
  BLOCK: HeaderNodeKind.BLOCK,
  LEAF: HeaderNodeKind.LEAF,
};
type TreeNode = HeaderNode & { children?: Array<TreeNode | null> | null };

/**
 * Give a header node, and its descendants, the `kind` the mapping reads.
 *
 * @param node - A header-tree node. The mapping tells GROUP, BLOCK and LEAF apart by `kind`; a node fetched
 *   without it (a thinner selection set) gets the kind its `__typename` names.
 * @returns The same node when nothing was missing, otherwise a copy with `kind` filled in.
 */
const withKind = (node: TreeNode): TreeNode => {
  const children = node.children?.map((child) => (child ? withKind(child) : child));
  const childrenChanged = (children ?? []).some((child, index) => child !== node.children?.[index]);
  const shape = node.kind ? null : resolveNodeShape(node);
  if (!shape && !childrenChanged) return node;
  return {
    ...node,
    ...(shape ? { kind: KIND_OF_SHAPE[shape] } : {}),
    ...(childrenChanged ? { children } : {}),
  } as TreeNode;
};

/**
 * Give every node of a section's tree the `kind` the mapping reads (see `withKind`).
 *
 * @param section - A page layout section.
 * @returns The same section when every node already had a `kind`, otherwise a copy with the kinds filled in.
 */
const withKinds = (section: PageLayoutSection): PageLayoutSection => {
  const tree = section.columnHeaderTree ?? [];
  const mapped = tree.map((node) => (node ? withKind(node as TreeNode) : node));
  return mapped.some((node, index) => node !== tree[index])
    ? { ...section, columnHeaderTree: mapped as PageLayoutSection['columnHeaderTree'] }
    : section;
};

/**
 * Read Preview Mode values back from the grid rows.
 *
 * @param state - The layout sections (for the column letters), the grid rows, and the selected granularity.
 *   A row carries a preview value as a numeric field named `<letter>_<granularity>`, lowercase
 *   (e.g. `a_mtd`); rows without a `serial_no` are skipped.
 * @returns The values keyed by deckline `serial_no`, then uppercase column letter; `undefined` when no row
 *   carries one (Preview Mode off) or there is no granularity, so production exports carry no values.
 */
export const previewValuesOf = (
  state: Pick<PageExportState, 'sections' | 'rowData' | 'granularity'>,
): AurigaPageData['previewValues'] => {
  if (!state.granularity) return undefined;
  const suffix = `_${state.granularity.toLowerCase()}`;
  const letters = letterOf(state.sections);
  const values: Record<string, Record<string, number>> = {};
  state.rowData.forEach((row) => {
    const serial = row.rawDecklineData?.serial_no;
    if (!serial) return;
    const fields = row as unknown as Record<string, unknown>;
    letters.forEach((letter) => {
      const value = fields[`${letter.toLowerCase()}${suffix}`];
      if (typeof value === 'number') (values[serial] ??= {})[letter] = value;
    });
  });
  return Object.keys(values).length > 0 ? values : undefined;
};

/**
 * Gather the page's working state into the shared mapping's input.
 *
 * @param state - What the page holds when Download is pressed (see `PageExportState`).
 * @returns The `AurigaPageData` for `renderAurigaPage`: the page (titled as the legacy export, the report
 *   header else the core page name), the non-null layout sections (every node given a `kind`), the
 *   working decklines, the footnotes
 *   without unsaved deletions, and the preview values when Preview Mode supplied any.
 */
export const toAurigaPageData = (state: PageExportState): AurigaPageData => {
  const previewValues = previewValuesOf(state);
  return {
    page: {
      page_name: state.page?.page_name ?? state.corePageName ?? null,
      // The legacy export's title: the report header, else the core page name.
      report_header: state.page?.report_header ?? state.corePageName ?? null,
      scale: state.page?.scale ?? null,
      page_grain: state.page?.page_grain ?? null,
    },
    layout: {
      sections: state.sections.filter((section): section is PageLayoutSection => section !== null).map(withKinds),
    },
    decklines: [...state.decklines],
    footnotes: state.footnotes.filter((footnote) => !footnote.metadata?.isDeleted),
    ...(previewValues ? { previewValues } : {}),
  };
};

/** The MIME type of the bytes `renderAurigaPage` returns. */
export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
